import React, { useEffect, useRef, useCallback, useState, useMemo, Fragment } from 'react';
import { Message } from '../Message';
import { TelegramLink } from '../TelegramLink/TelegramLink';
import { TitleDivider } from '../TitleDivider/TitleDivider';
import { TypingIndicator } from '../TypingIndicator';
import { useTheme, usePreset, useAssetUrl } from '../../lib/ThemeContext';
import { useChatStore } from '../../stores/chatStore';
import { streamingAnswerRef } from '../../hooks/streamingAnswerRef';
import { useTelegramHandoff } from '../../hooks/useTelegramHandoff';
import type { Message as MessageType } from '../../types/chat.types';

interface MessageListProps {
  messages: MessageType[];
  isTyping?: boolean;
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  bottomOffset?: number;
}

/**
 * Message list component with infinite scroll support
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isTyping = false,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  bottomOffset = 0,
}) => {
  const { telegramBotUrl, t, greeting } = useTheme();
  const preset = usePreset();
  const isNevi = preset === 'nevi';
  const assetUrl = useAssetUrl();
  const openTelegram = useTelegramHandoff(telegramBotUrl);
  const { chatTitle, titleMessageId, streamStage, streamArticles } = useChatStore();
  const hasStreamingDraft = Boolean(streamStage || streamArticles.length > 0);
  const greetingMessage = useGreetingMessage(greeting, messages);
  const showGreeting = Boolean(greetingMessage) && !hasMore && !(isLoading && messages.length === 0);
  const messagesStartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const shouldScrollToBottomRef = useRef<boolean>(true);
  const hasInitialScrolledRef = useRef<boolean>(false);

  // Track which messages have been rendered to animate only new ones
  const renderedMessagesRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef<boolean>(true);
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());

  // Update new messages tracking
  useEffect(() => {
    const currentIds = new Set(messages.map((m) => m.id));

    // On initial load, mark all messages as already rendered (no animation)
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      messages.forEach((m) => renderedMessagesRef.current.add(m.id));
      return;
    }

    const storeState = useChatStore.getState();
    const newIds = new Set<string>();

    messages.forEach((m) => {
      if (renderedMessagesRef.current.has(m.id)) return;
      // Suppress the appear animation for the bot message that just replaces a live stream —
      // the text was already on screen, any entrance animation reads as a jump.
      if (m.sender === 'bot' && storeState.justStreamedBot) {
        renderedMessagesRef.current.add(m.id);
        storeState.consumeBotStreamed();
        return;
      }
      newIds.add(m.id);
    });

    if (newIds.size > 0) {
      setNewMessageIds(newIds);
      // Add to rendered set after a short delay (after animation starts)
      setTimeout(() => {
        newIds.forEach((id) => renderedMessagesRef.current.add(id));
        setNewMessageIds(new Set());
      }, 400);
    }

    // Clean up old message IDs that are no longer in the list
    renderedMessagesRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        renderedMessagesRef.current.delete(id);
      }
    });
  }, [messages]);

  // Scroll to bottom on new messages.
  // Именно scrollTo до максимума, а не scrollIntoView якоря: панель ввода —
  // absolute-оверлей поверх низа списка, и когда она вырастает (превью
  // вложений), якорь остаётся «видимым» для scrollIntoView, хотя последнее
  // сообщение уже закрыто панелью — нужно докрутить на высоту нижнего padding.
  const scrollToBottom = useCallback((instant = false) => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: instant ? 'auto' : 'smooth' });
  }, []);

  // Check if user is near bottom
  const isNearBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;

    const threshold = 100;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Handle scroll event for infinite scroll
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Update scroll to bottom flag
    shouldScrollToBottomRef.current = isNearBottom();

    // Load more messages when scrolling to top
    if (container.scrollTop < 100 && hasMore && !isLoading && onLoadMore) {
      prevScrollHeightRef.current = container.scrollHeight;
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore, isNearBottom]);

  // Intersection observer for top sentinel (alternative to scroll event)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading && onLoadMore) {
          prevScrollHeightRef.current = containerRef.current?.scrollHeight || 0;
          onLoadMore();
        }
      },
      {
        root: containerRef.current,
        threshold: 0.1,
      },
    );

    const sentinel = messagesStartRef.current;
    if (sentinel && hasMore) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);

  // Maintain scroll position after loading more messages
  useEffect(() => {
    const container = containerRef.current;
    if (!container || prevScrollHeightRef.current === 0) return;

    const newScrollHeight = container.scrollHeight;
    const scrollDiff = newScrollHeight - prevScrollHeightRef.current;

    if (scrollDiff > 0) {
      container.scrollTop = scrollDiff;
      prevScrollHeightRef.current = 0;
    }
  }, [messages.length]);

  // Track previous message count to detect new messages (vs history loading)
  const prevMessageCountRef = useRef<number>(0);

  // Auto-scroll to bottom on new messages (if user was near bottom or new message appended at end)
  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const newCount = messages.length;
    prevMessageCountRef.current = newCount;

    // New message added at the end (not history loading at the top)
    const isNewMessageAppended = newCount > prevCount && prevCount > 0 && prevScrollHeightRef.current === 0;

    if (shouldScrollToBottomRef.current || isNewMessageAppended) {
      scrollToBottom();
    }
  }, [messages, isTyping, scrollToBottom]);

  // Initial scroll to bottom when messages first load
  useEffect(() => {
    if (messages.length > 0 && !hasInitialScrolledRef.current) {
      scrollToBottom(true); // instant scroll
      hasInitialScrolledRef.current = true;
    }
  }, [messages.length, scrollToBottom]);

  // Keep the view pinned to the bottom when the input area grows (e.g. attached image previews)
  useEffect(() => {
    scrollToBottom(true);
  }, [bottomOffset, scrollToBottom]);

  return (
    <div
      ref={containerRef}
      className="wc-h-full wc-overflow-y-auto wc-scrollbar-thin wc-textarea-ornament wc-overscroll-contain"
      onScroll={handleScroll}
    >
      <div
        className="wc-px-4 wc-pt-14 wc-flex wc-flex-col wc-gap-2 wc-min-h-full"
        style={{ paddingBottom: bottomOffset > 0 ? `${bottomOffset + 16}px` : '4rem' }}
      >
        {/* Top sentinel for intersection observer */}
        <div ref={messagesStartRef} className="wc-h-1 wc-mt-auto" />

        {/* Loading indicator at top */}
        {isLoading && hasMore && (
          <div className="wc-flex wc-justify-center wc-py-2">
            <div className="wc-text-sm wc-text-gray-500">{t.messageList.loading}</div>
          </div>
        )}

        {/* Company greeting opens the conversation history */}
        {showGreeting && greetingMessage && (
          <>
            <Message message={greetingMessage} />
            {messages.length === 0 && (
              <div className="wc-flex wc-justify-center wc-mt-2">
                <TelegramLink />
              </div>
            )}
          </>
        )}

        {/* Messages */}
        {!greeting && messages.length === 0 && !isLoading && !hasMore ? (
          <div className="wc-flex wc-items-center wc-justify-center wc-h-full">
            {isNevi ? (
              /* NeVi: welcome state with the mascot — Figma node 1911:20134 */
              <div
                className="wc-text-center wc-flex wc-flex-col wc-items-center wc-justify-end wc-h-full wc-w-full"
                style={{ gap: '12px', paddingBottom: '16px' }}
              >
                <img
                  src={assetUrl('/widget/nevi-mascot-full.webp')}
                  alt=""
                  style={{ width: '184px', height: 'auto', display: 'block' }}
                />
                <div>
                  <p
                    style={{
                      fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: '32px',
                      lineHeight: '48px',
                      color: '#F9AA66',
                      margin: 0,
                    }}
                  >
                    {t.messageList.welcome}
                  </p>
                  <p
                    style={{
                      fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                      fontSize: '18px',
                      lineHeight: '28px',
                      color: '#FBFBFB',
                      margin: '4px 0 0',
                    }}
                  >
                    {t.messageList.writeMessage}
                  </p>
                </div>
                {telegramBotUrl && (
                  <div className="wc-flex wc-items-center" style={{ gap: '16px', marginTop: '12px' }}>
                    <span
                      style={{
                        fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#FBFBFB',
                      }}
                    >
                      {t.messageList.alsoAvailable}
                    </span>
                    <a
                      href={telegramBotUrl}
                      onClick={openTelegram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wc-flex wc-items-center wc-no-underline"
                      style={{
                        gap: '8px',
                        padding: '6px 6px 6px 12px',
                        background: 'rgba(251, 251, 251, 0.05)',
                        border: '1px solid rgba(251, 251, 251, 0.15)',
                        borderRadius: '6px',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(251, 251, 251, 0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(251, 251, 251, 0.05)';
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#FBFBFB',
                        }}
                      >
                        {t.messageList.telegramBot}
                      </span>
                      {/* Telegram - Iconly Pro */}
                      <svg viewBox="0 0 17.0063 14.2706" fill="none" style={{ width: '16px', height: '14px' }}>
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.96578 11.1114L7.04578 12.8956C6.81075 13.1148 6.42825 12.9348 6.44992 12.6139C6.50992 11.7073 6.60325 10.3298 6.66575 9.56392C6.67575 9.44309 6.77742 9.30809 6.87411 9.22059C9.11661 7.18726 11.3641 5.15892 13.6108 3.12893C13.6941 3.05393 13.7924 2.86893 13.7408 2.77143C13.6824 2.66143 13.4133 2.69476 13.3016 2.76476C10.4483 4.56143 7.59495 6.35726 4.74575 8.15976C4.57075 8.26976 4.35659 8.29642 4.15992 8.23309C2.98492 7.85559 1.80325 7.49809 0.625754 7.12642C0.35492 7.04059 0.0332537 6.95559 0.00242032 6.61976C-0.0300797 6.26892 0.270754 6.10226 0.54242 5.99309C1.63742 5.55142 2.73992 5.12892 3.84075 4.70393C7.80078 3.1781 11.7608 1.6531 15.7208 0.128097C15.8533 0.0772641 16.0299 0.0455975 16.1683 0.0180975C16.5833 -0.0652358 16.9566 0.143098 16.9974 0.563098C17.0283 0.870598 16.9741 1.2131 16.9099 1.51726C16.0849 5.44142 15.2491 9.36392 14.4141 13.2873C14.1891 14.3473 13.6441 14.5481 12.7666 13.9006C11.6358 13.0656 8.96578 11.1114 8.96578 11.1114Z"
                          fill="#FBFBFB"
                        />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="wc-text-center wc-text-gray-500">
                <p className="wc-text-lg wc-mb-2">{t.messageList.welcome}</p>
                <p>{t.messageList.writeMessage}</p>
                {telegramBotUrl && (
                  <p className="wc-mt-3">
                    {t.messageList.alsoAvailable}{' '}
                    <a
                      href={telegramBotUrl}
                      onClick={openTelegram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wc-text-[var(--wc-primary-color)] hover:wc-underline"
                    >
                      {t.messageList.telegramBot}
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          [...messages].reverse().map((message, index) => {
            // Show title divider if:
            // 1. This is the oldest user message (first in reversed array, last chronologically) and we have a chatTitle
            // 2. OR this message triggered the current title (message.id === titleMessageId)
            const isOldestMessage = index === 0;
            const isOldestUserMessage = isOldestMessage && message.sender === 'user';
            const isTitleTriggerMessage = message.id === titleMessageId;

            // Show title above oldest user message (initial title) or above the message that triggered a title change
            const showTitle =
              (isOldestUserMessage && chatTitle && !titleMessageId) || // Initial title over first user message
              isTitleTriggerMessage; // Title change over triggering message

            return (
              <Fragment key={message.id}>
                {showTitle && chatTitle && <TitleDivider title={chatTitle} />}
                <Message message={message} animate={newMessageIds.has(message.id)} />
              </Fragment>
            );
          })
        )}

        {/* Progressive bot pipeline: stage → articles → streaming answer.
            Kept mounted so the typewriter loop has a stable DOM target throughout the stream. */}
        <StreamingDraft
          stage={streamStage}
          articles={streamArticles}
          visible={hasStreamingDraft}
          scrollHostRef={containerRef}
        />

        {/* Typing indicator — only if there is no streaming draft already showing progress */}
        {isTyping && !hasStreamingDraft && <TypingIndicator />}
      </div>
    </div>
  );
};

const GREETING_MESSAGE_ID = 'greeting';

/**
 * The greeting as a bot message dated with the start of the conversation (or the widget opening)
 */
function useGreetingMessage(greeting: string | undefined, messages: MessageType[]): MessageType | undefined {
  const [openedAt] = useState(() => new Date().toISOString());
  const oldestCreatedAt = messages.length > 0 ? messages[messages.length - 1].createdAt : undefined;

  return useMemo(() => {
    if (!greeting) return undefined;
    return {
      id: GREETING_MESSAGE_ID,
      content: greeting,
      sender: 'bot',
      createdAt: oldestCreatedAt ?? openedAt,
      messageType: 'text',
    };
  }, [greeting, oldestCreatedAt, openedAt]);
}

interface StreamingDraftProps {
  stage?: 'search_kb' | 'read_articles' | 'generate';
  articles: string[];
  visible: boolean;
  scrollHostRef: React.RefObject<HTMLDivElement | null>;
}

const STAGE_LABEL: Partial<Record<NonNullable<StreamingDraftProps['stage']>, string>> = {
  search_kb: '🔍 Ищу ответ в базе знаний…',
  read_articles: '📚 Изучаю статьи…',
  // 'generate' intentionally omitted — once the answer is streaming, the text itself is the feedback.
};

const StreamingDraft: React.FC<StreamingDraftProps> = ({ stage, articles, visible, scrollHostRef }) => {
  // Callback ref: fires every time the actual DOM node mounts/unmounts or swaps.
  // A plain useRef + one-shot useEffect leaves streamingAnswerRef.el pointing at a
  // stale node after React reconciles the JSX tree on visible=false → true.
  const setAnswerEl = useCallback(
    (el: HTMLDivElement | null) => {
      streamingAnswerRef.el = el;
      streamingAnswerRef.scrollHost = scrollHostRef.current;
      if (el) {
        // Seed an explicit height so the first transition animates from a known pixel value
        // (transitions from `auto` → `Xpx` are ignored by the browser).
        el.style.height = getComputedStyle(el).minHeight;
      }
    },
    [scrollHostRef],
  );

  if (!visible) return null;

  const stageLabel = stage ? STAGE_LABEL[stage] : undefined;
  const showStage = !!stageLabel && articles.length === 0;
  const showArticles = articles.length > 0;

  return (
    <div className="wc-flex wc-justify-start wc-mb-1">
      <div className="wc-max-w-[80%] wc-px-3 wc-py-2" style={{ contain: 'layout paint', willChange: 'contents' }}>
        {showStage && <div className="wc-text-sm wc-opacity-60">{stageLabel}</div>}
        {showArticles && (
          <div className="wc-text-sm wc-opacity-60 wc-mb-2">
            {articles.map((title, i) => (
              <div key={`${i}-${title}`} className="wc-py-0.5">
                📚 «{title}»
              </div>
            ))}
          </div>
        )}
        <div
          ref={setAnswerEl}
          className="wc-text-sm wc-leading-relaxed wc-whitespace-pre-wrap wc-break-words wc-message-content wc-stream-body"
        />
      </div>
    </div>
  );
};
